/**
 * Generatore di tracciato da seed — DETERMINISTICO (zero trigonometria).
 *
 * Colline dolci ottenute per "value noise": valori casuali (dal RNG seedato) a
 * intervalli regolari, interpolati con smootherstep. Somma di 3 ottave
 * (rolling / medie / dettaglio). Solo +,−,×,÷ e Math.floor → identico su ogni
 * motore JS. Niente somme di seni (rischio ULP cross-engine, docs §4.1).
 *
 * Sprint 0: un solo bioma, un solo tracciato. In Sprint 1 diventa una grammatica
 * a sezioni (flow / tecnica / rischio) con validatore-bot.
 */

import { mulberry32, hashSeed } from './rng';
import { smootherstep, lerp, len2 } from './dmath';
import { TRACK_DX, TRACK_LENGTH } from './constants';

export interface Terrain {
  /** Lunghezza del tracciato in px-mondo. */
  readonly length: number;
  /** groundY(x): quota del suolo (schermo verso il basso = valore maggiore). */
  heightAt(x: number): number;
  /** Tangente unitaria del pendio a x, orientata nel verso di marcia (+x). */
  slopeAt(x: number): { tx: number; ty: number };
}

const BASE_Y = 720; // quota base del suolo (px-mondo)

interface Octave {
  cell: number; // ampiezza della cella in numero di campioni
  amp: number; // ampiezza in px-mondo
}
const OCTAVES: readonly Octave[] = [
  { cell: 26, amp: 260 }, // colline lunghe: il ritmo della pompata
  { cell: 9, amp: 95 }, // ondulazioni medie
  { cell: 3.2, amp: 26 }, // dettaglio fine
];

const START_FLAT = 12; // campioni piatti in partenza (3-2-1-GO equo)

/**
 * @param seed       stringa deterministica del tracciato
 * @param difficulty 0..1 — scala l'ampiezza delle colline (più alto = colline più
 *                   grandi, più volo, più rischio). Default 0.5. In Sprint 1 sarà
 *                   sostituito da una grammatica a sezioni con validatore-bot.
 */
export function makeTerrain(seed: string, difficulty = 0.5): Terrain {
  const rng = mulberry32(hashSeed(seed));
  const n = Math.ceil(TRACK_LENGTH / TRACK_DX) + 4;
  const h = new Float64Array(n);
  const ampScale = 0.62 + 0.76 * difficulty; // ~0.62 (facile) .. ~1.38 (difficile)

  // Somma delle ottave.
  for (const oc of OCTAVES) {
    const cpCount = Math.ceil(n / oc.cell) + 2;
    const cps = new Float64Array(cpCount);
    for (let i = 0; i < cpCount; i++) cps[i] = (rng() * 2 - 1) * oc.amp * ampScale;
    for (let i = 0; i < n; i++) {
      const c = i / oc.cell;
      const i0 = Math.floor(c);
      const f = smootherstep(c - i0);
      h[i] = (h[i] ?? 0) + lerp(cps[i0]!, cps[i0 + 1]!, f);
    }
  }

  // Rampa d'ingresso piatta + baseline.
  for (let i = 0; i < n; i++) {
    const ramp = i < START_FLAT ? i / START_FLAT : 1;
    h[i] = (h[i] ?? 0) * ramp + BASE_Y;
  }

  const heightAt = (x: number): number => {
    if (x <= 0) return h[0]!;
    const fx = x / TRACK_DX;
    const i0 = Math.floor(fx);
    if (i0 >= n - 1) return h[n - 1]!;
    const f = fx - i0;
    return lerp(h[i0]!, h[i0 + 1]!, f);
  };

  const E = 6; // passo delle differenze finite per la pendenza
  const slopeAt = (x: number): { tx: number; ty: number } => {
    const dy = heightAt(x + E) - heightAt(x - E);
    const dx = 2 * E;
    const inv = 1 / len2(dx, dy);
    return { tx: dx * inv, ty: dy * inv };
  };

  return { length: TRACK_LENGTH, heightAt, slopeAt };
}
